/**
 * useBreakpointV2 - Unified responsive hooks for v2 components
 * 
 * These hooks use the unified breakpoint system (styles/breakpoints.ts).
 * Use these for all v2 components to ensure JS/CSS alignment.
 * 
 * Legacy components should continue using hooks from use-size-class.ts.
 */

import { useState, useEffect } from 'react';
import { BREAKPOINTS, MEDIA_QUERIES } from 'styles/breakpoints';

/**
 * Generic hook that listens to a media query
 */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    setMatches(mediaQuery.matches);

    const handler = (event: MediaQueryListEvent) => setMatches(event.matches);
    mediaQuery.addEventListener('change', handler);
    
    return () => mediaQuery.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

export function useIsIphoneSmallOnlyV2(): boolean {
  return useMediaQuery(MEDIA_QUERIES.IPHONE_SMALL_ONLY);
}

/**
 * Returns true when viewport is mobile only (< 768px)
 */
export function useIsMobileV2(): boolean {
  return useMediaQuery(MEDIA_QUERIES.MOBILE_ONLY);
}

/**
 * Returns true when viewport is tablet and up (>= 820px)
 */
export function useIsTabletUpV2(): boolean {
  return useMediaQuery(MEDIA_QUERIES.TABLET_UP);
}

/**
 * Returns true when viewport is tablet-large and up (>= 1024px)
 * This is where bento grid layout kicks in
 */
export function useIsTabletLargeUpV2(): boolean {
  return useMediaQuery(MEDIA_QUERIES.TABLET_LARGE_UP);
}

/**
 * Returns true when viewport is desktop and up (>= 1180px)
 */
export function useIsDesktopV2(): boolean {
  return useMediaQuery(MEDIA_QUERIES.DESKTOP_UP);
}

/**
 * Returns true when viewport is large desktop and up (>= 1440px)
 */
export function useIsLargeDesktopV2(): boolean {
  return useMediaQuery(MEDIA_QUERIES.LARGE_DESKTOP_UP);
}

/**
 * Returns the current breakpoint name
 */
export function useCurrentBreakpointV2(): 'mobile' | 'tablet' | 'desktop' | 'large-desktop' {
  const isLargeDesktop = useIsLargeDesktopV2();
  const isDesktop = useIsDesktopV2();
  const isTablet = useIsTabletUpV2();

  if (isLargeDesktop) return 'large-desktop';
  if (isDesktop) return 'desktop';
  if (isTablet) return 'tablet';
  return 'mobile';
}

// Re-export breakpoint values for convenience
export { BREAKPOINTS, MEDIA_QUERIES };
